import { useEffect, useState, useCallback } from "react";
import { api, adminAuthHeaders } from "@/lib/api";
import { Users, MousePointerClick, TrendingUp, Radio, Bot } from "lucide-react";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

const NAVY = "#1f3a8a";
const RED = "#dc2626";

export default function StatsPanel() {
  const [stats, setStats] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/stats", { headers: adminAuthHeaders() });
      setStats(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000); // odświeżanie co 20s
    return () => clearInterval(t);
  }, [load]);

  if (!stats) return <div className="p-8 text-center text-slate-400">Wczytywanie statystyk…</div>;

  const cards = [
    { testId: "stat-online", label: "Osoby online teraz", value: stats.online_now, icon: Radio, color: "text-green-600 bg-green-100" },
    { testId: "stat-today", label: "Wejścia dziś", value: stats.today_visits, icon: MousePointerClick, color: "text-[#1f3a8a] bg-[#1f3a8a]/10" },
    { testId: "stat-total", label: "Wejścia łącznie", value: stats.total_visits, icon: TrendingUp, color: "text-amber-600 bg-amber-100" },
    { testId: "stat-visitors", label: "Unikalni odwiedzający", value: stats.total_visitors, icon: Users, color: "text-purple-600 bg-purple-100" },
    { testId: "stat-bots", label: "Odfiltrowane boty (nie liczone)", value: stats.total_bots ?? 0, icon: Bot, color: "text-slate-500 bg-slate-100" },
  ];

  return (
    <div data-testid="stats-panel" className="p-6 space-y-8">
      {/* Karty */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {cards.map((c) => (
          <div key={c.testId} data-testid={c.testId} className="rounded-xl border border-slate-200 p-5 bg-white flex items-center gap-4">
            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${c.color}`}>
              <c.icon size={22} />
            </div>
            <div>
              <div className="text-3xl font-black text-slate-900 leading-none">{c.value}</div>
              <div className="text-xs text-slate-500 mt-1">{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Wykres dzienny */}
      <div data-testid="chart-daily" className="rounded-xl border border-slate-200 p-5 bg-white">
        <h3 className="font-bold text-slate-800 mb-1">Odwiedziny wg dni (ostatnie 30 dni)</h3>
        <p className="text-xs text-slate-400 mb-4">Liczba wejść oraz unikalnych osób każdego dnia</p>
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={stats.by_day} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id="gVisits" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={NAVY} stopOpacity={0.35} />
                <stop offset="95%" stopColor={NAVY} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
            <XAxis dataKey="day" tickFormatter={(d) => (d ? d.slice(5) : "")} tick={{ fontSize: 11, fill: "#94a3b8" }} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="visits" name="Wejścia" stroke={NAVY} strokeWidth={2} fill="url(#gVisits)" />
            <Area type="monotone" dataKey="visitors" name="Unikalne osoby" stroke={RED} strokeWidth={2} fillOpacity={0} />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Wykres godzinowy */}
      <div data-testid="chart-hourly" className="rounded-xl border border-slate-200 p-5 bg-white">
        <h3 className="font-bold text-slate-800 mb-1">Odwiedziny wg godzin (dzisiaj)</h3>
        <p className="text-xs text-slate-400 mb-4">Rozkład wejść w ciągu dnia (0–23)</p>
        <ResponsiveContainer width="100%" height={280}>
          <BarChart data={stats.by_hour} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} />
            <XAxis dataKey="hour" tick={{ fontSize: 10, fill: "#94a3b8" }} interval={1} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} />
            <Tooltip />
            <Legend />
            <Bar dataKey="visits" name="Wejścia" fill={NAVY} radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {/* Top strony / źródła */}
      <div className="grid md:grid-cols-2 gap-4">
        <div data-testid="top-pages" className="rounded-xl border border-slate-200 p-5 bg-white">
          <h3 className="font-bold text-slate-800 mb-3">Najczęściej odwiedzane podstrony</h3>
          {(!stats.top_pages || stats.top_pages.length === 0) ? (
            <p className="text-slate-400 text-sm">Brak danych.</p>
          ) : (
            <ul className="space-y-2">
              {stats.top_pages.map((p) => (
                <li key={p.path} className="flex items-center justify-between text-sm">
                  <code className="font-mono text-[#1f3a8a] bg-[#1f3a8a]/10 px-2 py-0.5 rounded truncate max-w-[220px]">{p.path || "/"}</code>
                  <span className="text-slate-700 font-semibold">{p.visits}<span className="text-slate-400 font-normal text-xs"> / {p.visitors} osób</span></span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div data-testid="top-referrers" className="rounded-xl border border-slate-200 p-5 bg-white">
          <h3 className="font-bold text-slate-800 mb-3">Skąd przychodzą odwiedzający</h3>
          {(!stats.top_referrers || stats.top_referrers.length === 0) ? (
            <p className="text-slate-400 text-sm">Brak danych.</p>
          ) : (
            <ul className="space-y-2">
              {stats.top_referrers.map((r, idx) => (
                <li key={`${r.source || "src"}-${idx}`} className="flex items-center justify-between text-sm">
                  <span className="text-slate-800 truncate max-w-[220px]">{r.source || "(bezpośrednio)"}</span>
                  <span className="text-slate-700 font-semibold">{r.visits}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
